import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  ViewChild,
  ElementRef,
  inject,
  PLATFORM_ID
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule, NavigationEnd } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ExploreService, ExploreItem } from '../../core/services/explore/explore.service';
import { LanguageService } from '../../core/services/language/language.service';
import { SeoService } from '../../core/services/seo/seo.service';
import { NormalNavbar } from '../../layout/normal-navbar/normal-navbar';
import { Footer } from '../../layout/footer/footer.component';
import { MediaCardComponent } from '../../shared/components/media-card/media-card.component';
import { CardSkeleton } from '../../layout/designs/card-skeleton/card-skeleton';

interface SubCategory {
  id: string;
  label: string;
  route: string;
}

@Component({
  selector: 'app-explore',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, NormalNavbar, Footer, MediaCardComponent, CardSkeleton],
  templateUrl: './explore.html',
  styleUrl: './explore.scss'
})
export class Explore implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollSentinel') scrollSentinel?: ElementRef<HTMLDivElement>;

  mediaType: 'movie' | 'tv' | 'person' = 'movie';
  category = 'popular';
  items: ExploreItem[] = [];
  currentPage = 1;
  totalPages = 999;
  isLoading = false;
  isLoadingMore = false;
  hasError = false;

  searchQuery = '';
  activeSearchQuery = '';
  totalSearchResults = 0;
  private readonly searchSubject = new Subject<string>();

  readonly initialSkeletons = Array(20).fill(0);
  readonly moreSkeletons = Array(8).fill(0);

  private readonly destroy$ = new Subject<void>();
  private readonly platformId = inject(PLATFORM_ID);
  public readonly languageService = inject(LanguageService);
  private readonly seoService = inject(SeoService);
  private observer?: IntersectionObserver;
  private lastFetchKey = '';

  private readonly onScrollListener = () => {
    this.onWindowScroll();
  };

  get movieCategories(): SubCategory[] {
    return [
      { id: 'popular', label: this.languageService.t('popularMovies'), route: '/movies/popular' },
      { id: 'now-playing', label: this.languageService.t('nowPlaying'), route: '/movies/now-playing' },
      { id: 'upcoming', label: this.languageService.t('upcoming'), route: '/movies/upcoming' },
      { id: 'top-rated', label: this.languageService.t('topRated'), route: '/movies/top-rated' }
    ];
  }

  get tvCategories(): SubCategory[] {
    return [
      { id: 'popular', label: this.languageService.t('popularTv'), route: '/tv/popular' },
      { id: 'airing-today', label: this.languageService.t('airingToday'), route: '/tv/airing-today' },
      { id: 'on-the-air', label: this.languageService.t('onTheAir'), route: '/tv/on-the-air' },
      { id: 'top-rated', label: this.languageService.t('topRated'), route: '/tv/top-rated' }
    ];
  }

  get personCategories(): SubCategory[] {
    return [
      { id: 'popular', label: this.languageService.t('popularPeople'), route: '/person/popular' }
    ];
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private exploreService: ExploreService,
    private titleService: Title,
    private metaService: Meta
  ) {}

  ngOnInit(): void {
    // Initial resolution on component creation
    this.resolveRoute();

    // Listen to router navigation events across sub-tab clicks and back/forward navigation
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.resolveRoute();
      });

    // Debounced search trigger for responsive search input
    this.searchSubject
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        this.executeSearch(query, 1);
      });

    // Reactively refresh explore data on language change
    this.languageService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.items.length > 0) {
          this.fetchData(1);
        }
      });

    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('scroll', this.onScrollListener, { passive: true });
      document.addEventListener('scroll', this.onScrollListener, { passive: true });
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initIntersectionObserver();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('scroll', this.onScrollListener);
      document.removeEventListener('scroll', this.onScrollListener);
    }
    this.observer?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Multi-viewport scroll event listener
  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.isLoading || this.isLoadingMore) {
      return;
    }
    if (this.totalPages > 1 && this.currentPage >= this.totalPages) {
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const clientHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight || 0;
    const scrollHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      document.documentElement.offsetHeight || 0,
      document.body.offsetHeight || 0
    );

    if (scrollTop + clientHeight >= scrollHeight - 900) {
      this.loadMore();
    }
  }

  private initIntersectionObserver(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const sentinel = this.scrollSentinel?.nativeElement;
    if (!sentinel) return;

    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          this.loadMore();
        }
      },
      {
        root: null,
        rootMargin: '800px', // Fetch next page 800px before reaching bottom
        threshold: 0
      }
    );

    this.observer.observe(sentinel);
  }

  onTabClick(sub: SubCategory): void {
    if (this.category === sub.id && !this.isSearching) return;
    this.searchQuery = '';
    this.activeSearchQuery = '';
    this.totalSearchResults = 0;
    this.category = sub.id;
    this.currentPage = 1;
    this.totalPages = 999;
    this.lastFetchKey = `${this.mediaType}:${this.category}`;
    this.updateSeoMetadata();
    this.fetchData(1);

    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private resolveRoute(): void {
    const rawUrl = this.router.url.split('?')[0].split('#')[0];

    // Determine media type
    if (rawUrl.startsWith('/tv')) {
      this.mediaType = 'tv';
    } else if (rawUrl.startsWith('/person') || rawUrl.startsWith('/persons')) {
      this.mediaType = 'person';
    } else {
      this.mediaType = 'movie';
    }

    // Determine category from URL segment
    const segments = rawUrl.split('/').filter(Boolean);
    let resolvedCategory = segments.length > 1 ? segments[1] : 'popular';

    // Normalize category slug aliases
    if (resolvedCategory === 'now_playing') resolvedCategory = 'now-playing';
    if (resolvedCategory === 'top_rated') resolvedCategory = 'top-rated';
    if (resolvedCategory === 'airing_today') resolvedCategory = 'airing-today';
    if (resolvedCategory === 'on_the_air') resolvedCategory = 'on-the-air';

    const fetchKey = `${this.mediaType}:${resolvedCategory}`;
    if (this.lastFetchKey === fetchKey && this.items.length > 0 && !this.isSearching) {
      return;
    }

    this.searchQuery = '';
    this.activeSearchQuery = '';
    this.totalSearchResults = 0;
    this.category = resolvedCategory;
    this.lastFetchKey = fetchKey;
    this.currentPage = 1;
    this.totalPages = 999;

    this.updateSeoMetadata();
    this.fetchData(1);

    // Ensure sentinel observer is bound after route transition
    setTimeout(() => {
      this.initIntersectionObserver();
    }, 150);
  }

  get currentSubCategories(): SubCategory[] {
    if (this.mediaType === 'tv') return this.tvCategories;
    if (this.mediaType === 'person') return this.personCategories;
    return this.movieCategories;
  }

  get pageTitle(): string {
    if (this.mediaType === 'movie') {
      switch (this.category) {
        case 'now-playing': return 'In Theatres & Now Playing Movies';
        case 'upcoming': return 'Upcoming Hollywood & Global Movies';
        case 'top-rated': return 'Top Rated Movies of All Time';
        default: return 'Popular Hollywood & Trending Movies';
      }
    }
    if (this.mediaType === 'tv') {
      switch (this.category) {
        case 'airing-today': return 'TV Shows Airing Today';
        case 'on-the-air': return 'Currently Airing TV Series';
        case 'top-rated': return 'Top Rated TV Series';
        default: return 'Popular TV Shows & Series';
      }
    }
    return 'Popular Celebrities & Hollywood Stars';
  }

  get pageDescription(): string {
    if (this.mediaType === 'movie') {
      switch (this.category) {
        case 'now-playing': return 'Browse current movies in theatres and cinemas worldwide. View trailers, ratings, and showtimes on The Scenetale.';
        case 'upcoming': return 'Explore anticipated upcoming Hollywood films, release dates, posters, and teasers on The Scenetale.';
        case 'top-rated': return 'Discover all-time highest rated movies voted by global film audiences on The Scenetale.';
        default: return 'Explore popular Hollywood movies, trending box office hits, ratings, and reviews on The Scenetale.';
      }
    }
    if (this.mediaType === 'tv') {
      switch (this.category) {
        case 'airing-today': return 'Watch episodes airing today across TV networks and streaming services on The Scenetale.';
        case 'on-the-air': return 'Follow currently broadcasting TV shows and ongoing seasons on The Scenetale.';
        case 'top-rated': return 'Discover critically acclaimed and fan-favorite television series on The Scenetale.';
        default: return 'Explore the most popular TV shows, trending streaming series, and season guides on The Scenetale.';
      }
    }
    return 'Explore trending actors, directors, and Hollywood celebrities with full filmography on The Scenetale.';
  }

  fetchData(page: number): void {
    if (page === 1) {
      this.isLoading = true;
      this.items = [];
    } else {
      this.isLoadingMore = true;
    }
    this.hasError = false;

    let request$;
    const lang = this.languageService.currentLanguage?.code || 'en-US';

    if (this.isSearching) {
      const q = this.activeSearchQuery;
      if (this.mediaType === 'tv') {
        request$ = this.exploreService.searchTvShows(q, page, lang);
      } else if (this.mediaType === 'person') {
        request$ = this.exploreService.searchPeople(q, page, lang);
      } else {
        request$ = this.exploreService.searchMovies(q, page, lang);
      }
    } else {
      if (this.mediaType === 'tv') {
        request$ = this.exploreService.getTvShows(this.category, page, lang);
      } else if (this.mediaType === 'person') {
        request$ = this.exploreService.getPeople(this.category, page, lang);
      } else {
        request$ = this.exploreService.getMovies(this.category, page, lang);
      }
    }

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        let rawResults: any[] = [];
        if (Array.isArray(res)) {
          rawResults = res;
        } else if (res && Array.isArray(res.results)) {
          rawResults = res.results;
        }

        const formatted = rawResults.map((item: any) => ({
          ...item,
          media_type: item.media_type || this.mediaType
        }));

        if (page === 1) {
          this.items = formatted;
        } else {
          // Avoid duplicate items by ID
          const existingIds = new Set(this.items.map(i => i.id));
          const newItems = formatted.filter((i: any) => !existingIds.has(i.id));
          this.items = [...this.items, ...newItems];
        }

        this.currentPage = res?.page || page;
        this.totalPages = res?.total_pages || 50;
        this.totalSearchResults = res?.total_results ?? formatted.length;
        this.isLoading = false;
        this.isLoadingMore = false;

        // Re-attach observer for next scroll page and proactively check viewport
        setTimeout(() => {
          this.initIntersectionObserver();
          this.onWindowScroll();
        }, 100);
      },
      error: (err) => {
        console.error('Failed to load explore data:', err);
        this.hasError = true;
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  loadMore(): void {
    if (this.isLoading || this.isLoadingMore) {
      return;
    }
    if (this.totalPages > 1 && this.currentPage >= this.totalPages) {
      return;
    }
    this.fetchData(this.currentPage + 1);
  }

  get isSearching(): boolean {
    return !!this.activeSearchQuery;
  }

  get searchPlaceholder(): string {
    if (this.mediaType === 'tv') {
      return 'Search TV shows (e.g. Breaking Bad, Stranger Things, Game of Thrones)...';
    }
    if (this.mediaType === 'person') {
      return 'Search actors & creators (e.g. Cillian Murphy, Scarlett Johansson, Christopher Nolan)...';
    }
    return 'Search movies (e.g. Inception, Oppenheimer, Interstellar)...';
  }

  get mediaLabel(): string {
    if (this.mediaType === 'tv') return 'TV shows';
    if (this.mediaType === 'person') return 'actors & people';
    return 'movies';
  }

  onSearchInput(val: string): void {
    this.searchQuery = val;
    const trimmed = (val || '').trim();
    if (!trimmed) {
      if (this.isSearching) {
        this.clearSearch();
      }
    } else {
      this.searchSubject.next(trimmed);
    }
  }

  submitSearch(): void {
    const trimmed = (this.searchQuery || '').trim();
    if (!trimmed) {
      if (this.isSearching) {
        this.clearSearch();
      }
      return;
    }
    this.executeSearch(trimmed, 1);
  }

  executeSearch(query: string, page: number = 1): void {
    if (!query) return;
    this.activeSearchQuery = query;
    this.currentPage = page;
    this.totalPages = 999;
    this.fetchData(page);

    if (isPlatformBrowser(this.platformId) && page === 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.activeSearchQuery = '';
    this.totalSearchResults = 0;
    this.currentPage = 1;
    this.totalPages = 999;
    this.lastFetchKey = `${this.mediaType}:${this.category}`;
    this.fetchData(1);

    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private updateSeoMetadata(): void {
    const title = this.pageTitle;
    const description = this.pageDescription;
    let canonicalPath = '/movies/popular';
    if (this.mediaType === 'tv') {
      canonicalPath = `/tv/${this.category}`;
    } else if (this.mediaType === 'person') {
      canonicalPath = `/person/${this.category}`;
    } else {
      canonicalPath = `/movies/${this.category}`;
    }

    this.seoService.setSeo({
      title,
      description,
      url: canonicalPath,
      type: 'website'
    });
  }
}
